import { useEffect, useMemo, type RefObject } from "react";
import { scrollElementIntoView } from "./scrollTopToReveal";

interface UseRevealHighlightedRowArgs {
  /** 1-based physical sheet rows to highlight (hovered flow's delineation.rows). */
  highlightedRows: number[] | undefined;
  /**
   * Rendered rows' absolute physical sheet row numbers, in render order
   * (`useSheetView`'s `rowNumbers` state). Hidden rows leave gaps.
   */
  rowNumbers: number[];
  /** Selected sheet tab — a same-buffer sheet-tab switch must re-run the reveal. */
  activeSheet: string;
  /** The scrollable spreadsheet container (`ExcelViewer`'s `containerRef`). */
  containerRef: RefObject<HTMLElement | null>;
  /**
   * The sticky `<thead>`, measured live so its opaque paint never occludes the
   * revealed row (STK-1602 finding 2). Omit for a container with no sticky
   * overlay.
   */
  headerRef?: RefObject<HTMLElement | null>;
}

/**
 * Scrolls the hovered flow's topmost highlighted source row into view — but
 * ONLY when it is off screen; an already-visible row must not move the sheet
 * a single pixel (STK-1602). Not virtualized, so the `<tr>` is always in the
 * DOM. Extracted out of `ExcelViewer` (STK-1602 final review) so the
 * dependency contract and the sticky-header inset are explicit and
 * unit-testable — mirroring the PDF side's `useHighlightFocus.ts`.
 *
 * ## Why `rowNumbers`, not `workbook`
 *
 * `firstHighlightedRow` alone is not enough: physical row numbers are
 * preserved 1:1 between the file buffer and a per-order slice
 * (`useSheetView` leaves gaps rather than renumbering), so swapping buffers
 * while the same flow stays hovered can leave the row NUMBER unchanged even
 * though the row's DOM position is now completely different (e.g. row 147
 * sits near the top of a 5-row slice but far down a 3000-row file). This is
 * also the PRIMARY motivating scenario — panel prev/next order navigation
 * while the mouse stays parked on the hovered flow row swaps `extraData` to a
 * brand-new buffer without toggling the view or changing the sheet name.
 *
 * The naive dependency, `workbook` (the active buffer's parsed workbook, a
 * `useMemo` in `useSheetView`), is WRONG: it changes ONE COMMIT BEFORE the
 * grid it describes. `sheetData`/`rowNumbers` are `useState`, seeded by
 * `useSheetView`'s `useLayoutEffect` — React flushes pending passive effects
 * (this one) before the layout-effect-triggered re-render paints, so an
 * effect keyed on `workbook` runs while the DOM still holds the PREVIOUS
 * buffer's rows, and never re-runs afterwards because `workbook` is already
 * unchanged by the time the new rows actually paint. Keying on `rowNumbers`
 * instead fixes this: it is a fresh array reference on every genuine re-seed
 * (buffer swap OR sheet-tab switch) — i.e. exactly the commit where the new
 * grid lands in the DOM — and it does NOT churn on zoom/keystroke/resize/
 * column-hide/cell-selection, none of which touch `useSheetView`'s
 * row-seeding effect. `activeSheet` stays alongside it for same-buffer
 * sheet-tab switches where `rowNumbers` could coincidentally be
 * reference-equal in a memo-unfriendly runtime; keeping it costs nothing since
 * `scrollElementIntoView` is a no-op when the target is already visible.
 */
export function useRevealHighlightedRow({
  highlightedRows,
  rowNumbers,
  activeSheet,
  containerRef,
  headerRef,
}: UseRevealHighlightedRowArgs) {
  const hi = useMemo(() => new Set(highlightedRows ?? []), [highlightedRows]);

  // Topmost highlighted row present in the RENDERED sheet. `rowNumbers` is in
  // render order, so the first hit is the one nearest the top — which is the
  // one to bring into view when a flow's source spans several rows.
  const firstHighlightedRow = useMemo(() => {
    for (const n of rowNumbers) {
      if (hi.has(n)) return n;
    }
    return null;
  }, [rowNumbers, hi]);

  useEffect(() => {
    if (firstHighlightedRow === null) return;
    const container = containerRef.current;
    const row = container?.querySelector<HTMLElement>(
      `[data-row-number="${firstHighlightedRow}"]`,
    );
    if (!container || !row) return;
    // Measured live rather than a hardcoded constant: the grid renders inside
    // a `transform: scale(zoom/100)` wrapper, and `getBoundingClientRect()`
    // reports the header's post-scale (visual) height — the honest value at
    // any zoom level.
    const insetTop = headerRef?.current?.getBoundingClientRect().height ?? 0;
    scrollElementIntoView(row, container, { insetTop });
  }, [firstHighlightedRow, rowNumbers, activeSheet, containerRef, headerRef]);

  return { hi, firstHighlightedRow };
}
